import React, { useState } from "react";
import styled from "styled-components";
import { Link, useLocation } from "react-router-dom";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { myTheme } from "src/styles/GlobalStyle";

type SidebarChild = {
  label: string;
  path?: string;
  children?: SidebarChild[];
};

type SidebarItem = {
  label: string;
  icon: React.ReactNode;
  path?: string;
  children?: SidebarChild[];
};

const SidebarItems: SidebarItem[] = [
  {
    label: "대시보드",
    icon: <DashboardIcon />,
    path: "/",
  },
  {
    label: "공통 관리",
    icon: <ExpandMoreIcon />,
    children: [
      { label: "메뉴 관리", path: "/common/menu" },
      { label: "FAQ 관리", path: "/common/faq" },
      { label: "공지사항 관리", path: "/common/notice" },
      { label: "공통 코드 관리", path: "/common/commonCode" },
      { label: "약관 관리", path: "/common/terms" },
      { label: "입점사 관리", path: "/delegate/manage" },
      { label: "브랜드 관리", path: "/common/brand" },
      { label: "컬렉션 관리", path: "/collection/manage" },
    ],
  },
  // ... 생략
  {
    label: "상품 관리",
    icon: <ExpandMoreIcon />,
    children: [
      {
        label: "기준 관리",
        children: [{ label: "SKU 관리", path: "/productManage/baseinfo/sku" }],
      },
      { label: "재고 관리", path: "/product/stock" },
    ],
  },
];

const Sidebar = () => {
  const location = useLocation();

  // ✅ 1단 오픈
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  // ✅ 2단 오픈 (어떤 1단 아래의 어떤 sub인지 key로 관리)
  const [openSubKey, setOpenSubKey] = useState<string | null>(null);

  const handleToggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
    // 1단 바뀌면 2단은 닫기
    setOpenSubKey(null);
  };

  const handleToggleSub = (parentIndex: number, subIdx: number) => {
    const key = `${parentIndex}-${subIdx}`;
    setOpenSubKey(openSubKey === key ? null : key);
  };

  const isActivePath = (path?: string) => !!path && location.pathname === path;

  return (
    <SidebarWrapper>
      <Logo>KNOLLO OMS</Logo>

      {SidebarItems.map((item, index) => {
        const hasChildren = !!item.children?.length;
        const isOpen = openIndex === index;

        return (
          <React.Fragment key={index}>
            {!hasChildren ? (
              <StyledLink to={item.path || "#"}>
                <MenuItem $active={isActivePath(item.path)}>
                  <div className="main-content">
                    {item.icon}
                    <span>{item.label}</span>
                  </div>
                </MenuItem>
              </StyledLink>
            ) : (
              <>
                <MenuItem onClick={() => handleToggle(index)}>
                  <div className="main-content">
                    {item.icon}
                    <span>{item.label}</span>
                  </div>
                  <ArrowIcon $isOpen={isOpen}>
                    <ChevronRightIcon />
                  </ArrowIcon>
                </MenuItem>

                {isOpen && (
                  <SubMenuWrapper>
                    {item.children?.map((sub, subIdx) => {
                      const subHasChildren = !!sub.children?.length;
                      const subKey = `${index}-${subIdx}`;
                      const subOpen = openSubKey === subKey;

                      // ✅ 2단 없는 경우(기존과 동일)
                      if (!subHasChildren) {
                        return (
                          <StyledLink key={subIdx} to={sub.path || "#"}>
                            <SubMenuItem $active={isActivePath(sub.path)}>{sub.label}</SubMenuItem>
                          </StyledLink>
                        );
                      }

                      // ✅ 2단 있는 경우: 토글 + 3단 렌더(여기선 depth=2까지만)
                      return (
                        <React.Fragment key={subIdx}>
                          <SubMenuItem
                            as="div"
                            onClick={() => handleToggleSub(index, subIdx)}
                            $active={false}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <span>{sub.label}</span>
                            <ArrowIcon $isOpen={subOpen}>
                              <ChevronRightIcon />
                            </ArrowIcon>
                          </SubMenuItem>

                          {subOpen && (
                            <SubSubMenuWrapper>
                              {sub.children?.map((leaf, leafIdx) => (
                                <StyledLink key={leafIdx} to={leaf.path || "#"}>
                                  <SubSubMenuItem $active={isActivePath(leaf.path)}>
                                    {leaf.label}
                                  </SubSubMenuItem>
                                </StyledLink>
                              ))}
                            </SubSubMenuWrapper>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </SubMenuWrapper>
                )}
              </>
            )}
          </React.Fragment>
        );
      })}
    </SidebarWrapper>
  );
};

export default Sidebar;

// --- Styled Components ---

const SidebarWrapper = styled.aside`
  width: 240px;
  flex: 0 0 240px;
  flex-shrink: 0;
  background-color: ${myTheme.colors.primary};
  display: flex;
  flex-direction: column;
  padding: 24px 0;
  height: 100vh;
  color: white;
  overflow-y: auto;
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  color: inherit;
`;

const MenuItem = styled.div<{ $active?: boolean }>`
  padding: 12px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  font-size: 14px;
  background-color: ${({ $active }) => ($active ? "rgba(255, 255, 255, 0.15)" : "transparent")};

  &:hover {
    background-color: ${myTheme.colors.primaryHover};
  }

  .main-content {
    display: flex;
    align-items: center;
  }

  svg {
    margin-right: 10px;
    font-size: 20px;
  }
`;

const SubMenuWrapper = styled.div`
  background-color: rgba(0, 0, 0, 0.1);
`;

const SubMenuItem = styled.div<{ $active?: boolean }>`
  padding: 10px 24px 10px 54px;
  font-size: 13px;
  cursor: pointer;
  color: ${({ $active }) => ($active ? "white" : "rgba(255, 255, 255, 0.7)")};
  background-color: ${({ $active }) => ($active ? "rgba(255, 255, 255, 0.1)" : "transparent")};
  font-weight: ${({ $active }) => ($active ? "600" : "400")};

  &:hover {
    color: white;
    background-color: rgba(255, 255, 255, 0.05);
  }
`;

// ✅ 2단(3레벨) wrapper/item
const SubSubMenuWrapper = styled.div`
  background-color: rgba(0, 0, 0, 0.16);
`;

const SubSubMenuItem = styled.div<{ $active?: boolean }>`
  padding: 9px 24px 9px 74px;
  font-size: 12.5px;
  cursor: pointer;
  color: ${({ $active }) => ($active ? "white" : "rgba(255, 255, 255, 0.7)")};
  background-color: ${({ $active }) => ($active ? "rgba(255, 255, 255, 0.12)" : "transparent")};
  font-weight: ${({ $active }) => ($active ? "700" : "400")};

  &:hover {
    color: white;
    background-color: rgba(255, 255, 255, 0.06);
  }
`;

const ArrowIcon = styled.div<{ $isOpen: boolean }>`
  display: flex;
  transform: ${({ $isOpen }) => ($isOpen ? "rotate(90deg)" : "rotate(0deg)")};
  transition: transform 0.2s ease;
  svg {
    margin-right: 0;
  }
`;

const Logo = styled.div`
  font-size: 20px;
  font-weight: bold;
  padding: 0 24px 24px;
`;
